/**
 * HTML-to-PDF renderer for complex Indic scripts (Tamil, Hindi, Telugu, Kannada, Malayalam).
 *
 * jsPDF cannot shape complex scripts (no HarfBuzz), so Tamil/Indic conjuncts render broken.
 * This utility renders a hidden, browser-styled HTML layout (which DOES shape correctly),
 * captures it with html2canvas, and slices the image across A4 pages into a jsPDF document.
 *
 * Only used when Indic text is detected — pure-English PDFs keep using fast native jsPDF text.
 */
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { detectScripts } from './pdfMultilingual';

const SCRIPT_FONT = {
  tamil:      { family: 'Noto Sans Tamil',      url: '/fonts/NotoSansTamil-Regular.ttf' },
  devanagari: { family: 'Noto Sans Devanagari', url: '/fonts/NotoSansDevanagari-Regular.ttf' },
  telugu:     { family: 'Noto Sans Telugu',     url: '/fonts/NotoSansTelugu-Regular.ttf' },
  kannada:    { family: 'Noto Sans Kannada',    url: '/fonts/NotoSansKannada-Regular.ttf' },
  malayalam:  { family: 'Noto Sans Malayalam',  url: '/fonts/NotoSansMalayalam-Regular.ttf' },
};

const FONT_STACK = "'Helvetica Neue',Arial,'Noto Sans Tamil','Noto Sans Devanagari','Noto Sans Telugu','Noto Sans Kannada','Noto Sans Malayalam',sans-serif";

const _loaded = {};

// True when the text contains any supported Indic script.
export const hasIndicScript = (text) => detectScripts(String(text || '')).length > 0;

// Load (once) the Noto font files needed for the scripts present in `text` via the FontFace API,
// guaranteeing they are ready before html2canvas captures.
const ensureFontsForText = async (text) => {
  if (!('FontFace' in window) || !document.fonts) return;
  const scripts = detectScripts(String(text || ''));
  await Promise.all(scripts.map(async (s) => {
    const def = SCRIPT_FONT[s];
    if (!def || _loaded[def.family]) return;
    try {
      const ff = new FontFace(def.family, `url(${def.url})`);
      await ff.load();
      document.fonts.add(ff);
      _loaded[def.family] = true;
    } catch (e) {
      console.warn('[pdfHtmlRender] Font load failed:', def.family, e);
    }
  }));
};

const esc = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Load an image (CDN URL or data URL) and re-encode it through a canvas to a clean
 * RGBA PNG data URL. jsPDF's addImage is unreliable with indexed/palette PNGs
 * (colortype 3) and certain transparency — it often renders them as a solid colour
 * block. Re-encoding via canvas normalises the image so jsPDF renders it correctly.
 * Returns { dataUrl, width, height } or null on failure.
 */
export const loadImageForPdf = (src) => new Promise((resolve) => {
  if (!src) { resolve(null); return; }
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    try {
      const w = img.naturalWidth || img.width;
      const h = img.naturalHeight || img.height;
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve({ dataUrl: canvas.toDataURL('image/png'), width: w, height: h });
    } catch (e) {
      console.warn('[pdfHtmlRender] image re-encode failed:', e);
      resolve(null);
    }
  };
  img.onerror = () => resolve(null);
  img.src = src;
});

// Render an HTML string to a multi-page A4 PDF and save it.
export const generatePdfFromHtml = async (htmlString, { orientation = 'p', filename = 'document.pdf', fontText = '' } = {}) => {
  await ensureFontsForText(fontText || htmlString);

  const a4 = orientation === 'l' ? { w: 297, h: 210 } : { w: 210, h: 297 };
  const margin = 8;
  const contentWidthMM = a4.w - margin * 2;
  const MM_TO_PX = 3.7795275591; // 96 dpi
  const pxWidth = Math.round(contentWidthMM * MM_TO_PX);

  const container = document.createElement('div');
  container.style.cssText = `position:fixed;left:-99999px;top:0;width:${pxWidth}px;background:#ffffff;font-family:${FONT_STACK};`;
  container.innerHTML = htmlString;
  document.body.appendChild(container);

  try {
    if (document.fonts && document.fonts.ready) {
      try { await document.fonts.ready; } catch (_) { /* noop */ }
    }
    const canvas = await html2canvas(container, {
      scale: 2,
      backgroundColor: '#ffffff',
      useCORS: true,
      logging: false,
      windowWidth: pxWidth,
    });

    const pdf = new jsPDF(orientation, 'mm', 'a4');
    const pxPerMM = canvas.width / contentWidthMM;
    const pageContentHeightPx = (a4.h - margin * 2) * pxPerMM;

    let rendered = 0;
    let page = 0;
    while (rendered < canvas.height - 1) {
      const sliceH = Math.min(pageContentHeightPx, canvas.height - rendered);
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = canvas.width;
      pageCanvas.height = Math.ceil(sliceH);
      const ctx = pageCanvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
      ctx.drawImage(canvas, 0, rendered, canvas.width, sliceH, 0, 0, canvas.width, sliceH);

      if (page > 0) pdf.addPage();
      pdf.addImage(pageCanvas.toDataURL('image/jpeg', 0.92), 'JPEG', margin, margin, contentWidthMM, sliceH / pxPerMM);
      rendered += sliceH;
      page++;
    }

    pdf.save(filename);
  } finally {
    document.body.removeChild(container);
  }
};

/**
 * Tabular report → PDF (used by Reports.js).
 * sections: [{ title?, head: [...], body: [[...]], align?: ['c'|'r'|''...], headColor? }]
 */
export const exportTableReportPdf = async ({ title, metaLines = [], sections = [], orientation = 'l', filename }) => {
  const sectionsHtml = sections.map((sec) => {
    const headColor = sec.headColor || '#4f46e5';
    const align = sec.align || [];
    const headHtml = `<tr>${sec.head.map((h, i) => `<th class="${align[i] === 'r' ? 'r' : align[i] === 'c' ? 'c' : ''}">${esc(h)}</th>`).join('')}</tr>`;
    const bodyHtml = sec.body.map((row) =>
      `<tr>${row.map((c, i) => `<td class="${align[i] === 'r' ? 'r' : align[i] === 'c' ? 'c' : ''}">${esc(c)}</td>`).join('')}</tr>`
    ).join('');
    const secTitle = sec.title ? `<div class="sec-title">${esc(sec.title)}</div>` : '';
    return `${secTitle}<table style="--hc:${headColor}"><thead>${headHtml}</thead><tbody>${bodyHtml}</tbody></table>`;
  }).join('');

  const html = `
    <style>
      .rpt{color:#1e293b;padding:6px 4px;}
      .rpt .title{font-size:18px;font-weight:700;}
      .rpt .meta{font-size:11px;color:#475569;margin-top:2px;}
      .rpt table{width:100%;border-collapse:collapse;margin-top:10px;font-size:11px;}
      .rpt th{background:var(--hc);color:#ffffff;text-align:left;padding:5px 6px;border:1px solid #cbd5e1;font-weight:600;}
      .rpt td{padding:4px 6px;border:1px solid #e2e8f0;vertical-align:top;}
      .rpt td.r,.rpt th.r{text-align:right;}
      .rpt td.c,.rpt th.c{text-align:center;}
      .rpt tbody tr:nth-child(even){background:#f8fafc;}
      .rpt .sec-title{font-size:13px;font-weight:600;margin-top:14px;}
    </style>
    <div class="rpt">
      <div class="title">${esc(title)}</div>
      ${metaLines.map((m) => `<div class="meta">${esc(m)}</div>`).join('')}
      ${sectionsHtml}
    </div>`;

  const fontText = [title, ...metaLines, ...sections.flatMap((s) => [...(s.head || []), ...s.body.flatMap((r) => r)])].join(' ');
  await generatePdfFromHtml(html, { orientation, filename, fontText });
};

// ---- Number to words (Indian) for invoice amount ----
const numberToWords = (num) => {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  if (num === 0) return 'Zero';
  const lt1000 = (n) => {
    if (n === 0) return '';
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + lt1000(n % 100) : '');
  };
  const conv = (n) => {
    if (n >= 10000000) return conv(Math.floor(n / 10000000)) + ' Crore ' + conv(n % 10000000);
    if (n >= 100000) return conv(Math.floor(n / 100000)) + ' Lakh ' + conv(n % 100000);
    if (n >= 1000) return conv(Math.floor(n / 1000)) + ' Thousand ' + conv(n % 1000);
    return lt1000(n);
  };
  const rupees = Math.floor(num);
  const paise = Math.round((num - rupees) * 100);
  let result = conv(rupees) + ' Rupees';
  if (paise > 0) result += ' and ' + conv(paise) + ' Paise';
  return result + ' Only';
};

const fmtDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
const money = (n) => 'Rs. ' + (Number(n) || 0).toFixed(2);

/**
 * A4 GST Invoice → PDF (used by ThermalBillPrint & A4InvoicePDF when Indic text present).
 * Computes the same way as the native A4 generator but renders via HTML so Tamil shapes correctly.
 */
export const exportInvoiceHtmlPdf = async ({ bill, businessInfo, billSettings, customerDetails }) => {
  const isEstimation = bill?.is_estimation || bill?.status === 'estimation';
  const taxMethod = billSettings?.tax_method || 'inclusive';
  const items = bill?.items || [];

  const custName = bill?.customer_name || 'Walk-in Customer';
  const custMobile = bill?.customer_mobile || customerDetails?.mobile || '';
  const custEmail = bill?.customer_email || customerDetails?.email || '';
  const custAddress = bill?.customer_address || customerDetails?.address || '';
  const custGST = bill?.customer_gst_number || bill?.customer_gst || customerDetails?.gst_number || '';

  // Pre-load logo & signature as clean base64 data URLs (avoids CORS load failures in html2canvas)
  const noHeader = billSettings?.a4_no_header;
  const logoData = (!noHeader && businessInfo?.logo) ? await loadImageForPdf(businessInfo.logo) : null;
  const sigData = billSettings?.signature_url ? await loadImageForPdf(billSettings.signature_url) : null;

  // Per-item rows (S.No, Product, Qty, Unit, Rate, Discount, Taxable Amt) — matches native A4 layout
  const rowsHtml = items.map((item, idx) => {
    const baseAmount = item.quantity * item.price;
    const itemDiscount = item.item_discount || 0;
    const afterDiscount = baseAmount - itemDiscount;
    const discountPercent = baseAmount > 0 ? ((itemDiscount / baseAmount) * 100) : 0;
    let taxableAmount;
    if (taxMethod === 'inclusive') {
      taxableAmount = afterDiscount / (1 + ((item.tax_percent || 0) / 100));
    } else {
      taxableAmount = afterDiscount;
    }
    const details = [];
    if (item.description) details.push(esc(item.description));
    if (item.serial_number_values?.length > 0) details.push(`S/N: ${esc(item.serial_number_values.join(', '))}`);
    if (item.hsn_code) details.push(`HSN: ${esc(item.hsn_code)}`);
    if (item.mrp && item.mrp > 0 && billSettings?.a4_show_mrp !== false) details.push(`MRP: ${(item.mrp).toFixed(2)}`);
    if (item.tax_percent) details.push(`GST: ${item.tax_percent}%`);
    const detailLine = details.length ? `<div class="muted">${details.join(' | ')}</div>` : '';
    return `<tr>
      <td class="c">${idx + 1}</td>
      <td><div class="pname">${esc(item.product_name)}</div>${detailLine}</td>
      <td class="c">${item.quantity}${item.stock_quantity != null ? ` (${item.stock_quantity} ${esc(item.stock_unit || 'pcs')})` : ''}</td>
      <td class="c">${esc(item.unit || 'PCS')}</td>
      <td class="r">${(item.price || 0).toFixed(2)}</td>
      <td class="r">${itemDiscount > 0 ? `${discountPercent.toFixed(1)}% / ${itemDiscount.toFixed(2)}` : '-'}</td>
      <td class="r">${taxableAmount.toFixed(2)}</td>
    </tr>`;
  }).join('');

  // GST groups + totals
  const taxGroups = {};
  items.forEach((item) => {
    const rate = item.tax_percent || 0;
    const afterDiscount = (item.quantity * item.price) - (item.item_discount || 0);
    let taxable, tax;
    if (taxMethod === 'inclusive') {
      taxable = afterDiscount / (1 + rate / 100);
      tax = afterDiscount - taxable;
    } else {
      taxable = afterDiscount;
      tax = (afterDiscount * rate) / 100;
    }
    if (!taxGroups[rate]) taxGroups[rate] = { taxable: 0, cgst: 0, sgst: 0 };
    taxGroups[rate].taxable += taxable;
    taxGroups[rate].cgst += tax / 2;
    taxGroups[rate].sgst += tax / 2;
  });
  const totalTaxable = Object.values(taxGroups).reduce((s, v) => s + v.taxable, 0);
  const totalCGST = Object.values(taxGroups).reduce((s, v) => s + v.cgst, 0);
  const totalSGST = Object.values(taxGroups).reduce((s, v) => s + v.sgst, 0);
  const totalGST = totalCGST + totalSGST;

  const subtotal = items.reduce((s, item) => s + (item.quantity * item.price), 0);
  const totalItemDiscount = items.reduce((s, item) => s + (item.item_discount || 0), 0);
  const billDiscountAmt = bill?.discount_amount || 0;
  const grandRaw = taxMethod === 'inclusive'
    ? (subtotal - totalItemDiscount - billDiscountAmt)
    : (subtotal - totalItemDiscount - billDiscountAmt + totalGST);
  const roundedTotal = Math.round(grandRaw);
  const roundOff = roundedTotal - grandRaw;

  const gstRows = Object.entries(taxGroups).map(([rate, v]) =>
    `<tr><td class="c">${rate}%</td><td class="r">${v.taxable.toFixed(2)}</td><td class="r">${v.cgst.toFixed(2)}</td><td class="r">${v.sgst.toFixed(2)}</td><td class="r">${(v.taxable + v.cgst + v.sgst).toFixed(2)}</td></tr>`
  ).join('');

  const addr1 = [businessInfo?.address, businessInfo?.city].filter(Boolean).join(', ');
  const addr2 = [businessInfo?.district, businessInfo?.state, businessInfo?.postal_code ? `- ${businessInfo.postal_code}` : ''].filter(Boolean).join(', ');
  const contact = [businessInfo?.mobile_number || businessInfo?.mobile, businessInfo?.email].filter(Boolean).join(' | ');

  const payMethod = !isEstimation ? (bill?.payment_modes?.map((p) => p.mode).join(', ') || 'Cash') : '';
  const payDisplay = payMethod ? payMethod.charAt(0).toUpperCase() + payMethod.slice(1) : '';

  const logoImg = logoData?.dataUrl
    ? `<img src="${logoData.dataUrl}" style="width:68px;height:68px;object-fit:contain;margin-right:12px;" />` : '';

  const headerHtml = noHeader ? `
    <div style="text-align:center;font-size:20px;font-weight:700;">${isEstimation ? 'ESTIMATION' : 'GST INVOICE'}</div>
    <div style="display:flex;justify-content:space-between;font-size:11px;margin-top:6px;">
      <span class="strong">${isEstimation ? 'Est' : 'Invoice'} No: ${esc(bill?.bill_number || '')}</span>
      <span class="strong">Date: ${fmtDate(bill?.created_at)}</span>
    </div>
  ` : `
    <div class="hdr">
      <div class="hdr-left">
        ${logoImg}
        <div>
          ${billSettings?.a4_show_company_name !== false ? `<div class="biz">${esc(businessInfo?.business_name || 'Business Name')}</div>` : ''}
          ${billSettings?.a4_show_address !== false && addr1 ? `<div class="muted">${esc(addr1)}</div>` : ''}
          ${billSettings?.a4_show_address !== false && addr2 ? `<div class="muted">${esc(addr2)}</div>` : ''}
          ${contact ? `<div class="muted strong">${esc(contact)}</div>` : ''}
          ${billSettings?.a4_show_gst_number !== false && businessInfo?.gst_number ? `<div class="strong">GSTIN: ${esc(businessInfo.gst_number)}</div>` : ''}
        </div>
      </div>
      <div class="hdr-right">
        <div class="doctype">${isEstimation ? 'ESTIMATION' : 'GST INVOICE'}</div>
        <div><span class="strong">${isEstimation ? 'Est' : 'Invoice'} No:</span> ${esc(bill?.bill_number || '')}</div>
        <div><span class="strong">Date:</span> ${fmtDate(bill?.created_at)}</div>
        ${payDisplay ? `<div><span class="strong">Payment:</span> ${esc(payDisplay)}</div>` : ''}
      </div>
    </div>`;

  // Bank details (only when present) — matches native bottom row
  const hasBank = businessInfo?.bank_account_number || businessInfo?.bank_name;
  const bankLines = [];
  if (businessInfo?.bank_name) bankLines.push(`Bank: ${esc(businessInfo.bank_name)}`);
  if (businessInfo?.bank_account_name) bankLines.push(`A/C Name: ${esc(businessInfo.bank_account_name)}`);
  if (businessInfo?.bank_account_number) bankLines.push(`A/C No: ${esc(businessInfo.bank_account_number)}`);
  if (businessInfo?.bank_branch_name) bankLines.push(`Branch: ${esc(businessInfo.bank_branch_name)}`);
  if (businessInfo?.bank_ifsc_code) bankLines.push(`IFSC: ${esc(businessInfo.bank_ifsc_code)}`);
  const bankHtml = hasBank ? `
    <div class="foot-col">
      <div class="strong">BANK DETAILS:</div>
      ${bankLines.map((l) => `<div class="muted">${l}</div>`).join('')}
    </div>` : '';

  const html = `
    <style>
      .inv{color:#1e293b;padding:6px;font-size:12px;}
      .inv .muted{color:#334155;font-size:10px;line-height:1.4;}
      .inv .strong{font-weight:700;}
      .inv .hdr{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:1.5px solid #94a3b8;padding-bottom:8px;}
      .inv .hdr-left{display:flex;align-items:flex-start;max-width:62%;}
      .inv .biz{font-size:18px;font-weight:800;color:#000;}
      .inv .hdr-right{text-align:right;font-size:11px;min-width:150px;}
      .inv .doctype{font-size:15px;font-weight:800;color:#000;margin-bottom:4px;}
      .inv .boxes{display:flex;gap:8px;margin-top:10px;}
      .inv .box{flex:1;border:1px solid #b6bfcc;border-radius:2px;padding:7px 8px;}
      .inv .box .label{font-weight:700;font-size:11px;margin-bottom:3px;}
      .inv .box .nm{font-weight:700;font-size:12px;}
      .inv table{width:100%;border-collapse:collapse;margin-top:12px;font-size:10px;}
      .inv th{background:#f1f5f9;color:#0f172a;text-align:left;padding:5px 6px;border:1px solid #b6bfcc;font-weight:700;}
      .inv td{padding:5px 6px;border:1px solid #cbd5e1;vertical-align:top;}
      .inv td.r,.inv th.r{text-align:right;}
      .inv td.c,.inv th.c{text-align:center;}
      .inv .pname{font-weight:600;font-size:11px;}
      .inv .lower{display:flex;gap:16px;margin-top:14px;align-items:flex-start;}
      .inv .gst-wrap{flex:1.15;}
      .inv .sum{flex:0.85;padding-top:4px;}
      .inv .sum .ln{display:flex;justify-content:space-between;padding:2.5px 0;font-size:11px;}
      .inv .sum .net{display:flex;justify-content:space-between;border-top:1.5px solid #000;padding:6px 0 0;margin-top:4px;font-weight:800;font-size:13px;}
      .inv .words{margin-top:14px;font-size:11px;}
      .inv .footrow{margin-top:16px;display:flex;justify-content:space-between;gap:16px;border-top:1px solid #b6bfcc;padding-top:8px;font-size:10px;color:#475569;}
      .inv .foot-col{flex:1;}
      .inv .sign{text-align:right;flex:1;}
      .inv .foot{text-align:center;font-size:9px;color:#94a3b8;margin-top:16px;}
    </style>
    <div class="inv">
      ${headerHtml}
      <div class="boxes">
        <div class="box">
          <div class="label">Bill To:</div>
          <div class="nm">${esc(custName)}</div>
          ${custAddress ? `<div class="muted">${esc(custAddress)}</div>` : ''}
          ${custMobile ? `<div class="muted">Ph: ${esc(custMobile)}</div>` : ''}
          ${custEmail ? `<div class="muted">Email: ${esc(custEmail)}</div>` : ''}
          ${custGST ? `<div class="strong">GST: ${esc(custGST)}</div>` : ''}
        </div>
        <div class="box">
          <div class="label">Ship To:</div>
          <div class="nm">${esc(custName)}</div>
          ${custAddress ? `<div class="muted">${esc(custAddress)}</div>` : ''}
        </div>
      </div>
      <table>
        <thead><tr>
          <th class="c">S.No</th><th>Product Description</th><th class="c">Qty</th><th class="c">Unit</th>
          <th class="r">Rate</th><th class="r">Discount</th><th class="r">Taxable Amt</th>
        </tr></thead>
        <tbody>${rowsHtml}</tbody>
      </table>
      <div class="lower">
        <div class="gst-wrap">
          <div class="strong" style="margin-bottom:4px;">GST Summary</div>
          <table style="margin-top:0;">
            <thead><tr><th class="c">GST %</th><th class="r">Taxable</th><th class="r">CGST</th><th class="r">SGST</th><th class="r">Total</th></tr></thead>
            <tbody>
              ${gstRows}
              <tr><td class="c strong">Total</td><td class="r strong">${totalTaxable.toFixed(2)}</td><td class="r strong">${totalCGST.toFixed(2)}</td><td class="r strong">${totalSGST.toFixed(2)}</td><td class="r strong">${(totalTaxable + totalGST).toFixed(2)}</td></tr>
            </tbody>
          </table>
        </div>
        <div class="sum">
          <div class="strong" style="margin-bottom:4px;">Summary</div>
          <div class="ln"><span>Sub Total:</span><span>${subtotal.toFixed(2)}</span></div>
          ${totalItemDiscount > 0 ? `<div class="ln"><span>Item Discount:</span><span>- ${totalItemDiscount.toFixed(2)}</span></div>` : ''}
          ${billDiscountAmt > 0 ? `<div class="ln"><span>Bill Discount:</span><span>- ${billDiscountAmt.toFixed(2)}</span></div>` : ''}
          <div class="ln"><span>CGST:</span><span>${totalCGST.toFixed(2)}</span></div>
          <div class="ln"><span>SGST:</span><span>${totalSGST.toFixed(2)}</span></div>
          ${Math.abs(roundOff) >= 0.01 ? `<div class="ln"><span>Round Off:</span><span>${(roundOff >= 0 ? '+' : '') + roundOff.toFixed(2)}</span></div>` : ''}
          <div class="net"><span>NET TOTAL:</span><span>${money(roundedTotal)}</span></div>
        </div>
      </div>
      <div class="words"><span class="strong">Rupees:</span> ${esc(numberToWords(roundedTotal))}</div>
      <div class="footrow">
        ${bankHtml}
        <div class="foot-col">
          <div class="strong">Terms &amp; Conditions:</div>
          <div>${esc(billSettings?.terms_text || 'Goods once sold will not be exchanged or returned.')}</div>
        </div>
        <div class="sign">
          <div class="strong">For ${esc(businessInfo?.business_name || 'Business')}</div>
          ${sigData?.dataUrl ? `<img src="${sigData.dataUrl}" style="height:40px;object-fit:contain;margin:6px 0 0 auto;display:block;" />` : '<div style="margin-top:26px;"></div>'}
          <div>Authorised Signatory</div>
        </div>
      </div>
      <div class="foot">This is a computer generated document.</div>
    </div>`;

  const fontText = [businessInfo?.business_name, addr1, addr2, custName, custAddress, bill?.notes, ...items.flatMap((i) => [i.product_name, i.description])].join(' ');
  const filename = `${isEstimation ? 'Estimation' : 'Invoice'}_${bill?.bill_number}_${fmtDate(bill?.created_at).replace(/\//g, '-')}.pdf`;
  await generatePdfFromHtml(html, { orientation: 'p', filename, fontText });
};
